import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: attach token from localStorage
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401: try refresh token once, then retry original request
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve());
  failedQueue = [];
};

api.interceptors.response.use(
  res => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(api(originalRequest)), reject });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) throw new Error('No refresh token');
        const { data } = await api.post('/auth/refresh', { refresh_token: refreshToken });
        if (data.access_token) localStorage.setItem('access_token', data.access_token);
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
};

// Sites
export const sitesApi = {
  list: () => api.get('/sites'),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
  uploadLogo: (siteId, mode, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/sites/${siteId}/logo/${mode}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (siteId, mode) => api.delete(`/sites/${siteId}/logo/${mode}`),
};

// Entities
export const entitiesApi = {
  list: (type) => api.get('/entities', { params: type ? { entity_type: type } : {} }),
  create: (data) => api.post('/entities', data),
  update: (id, data) => api.put(`/entities/${id}`, data),
  delete: (id) => api.delete(`/entities/${id}`),
  quickCreate: (data) => api.post('/entities/quick-create', data),
};

// Sub-categories
export const subCategoriesApi = {
  list: (parentCategory) => api.get('/sub-categories', { params: parentCategory ? { parent_category: parentCategory } : {} }),
  create: (data) => api.post('/sub-categories', data),
  delete: (id) => api.delete(`/sub-categories/${id}`),
};

// Entries
export const entriesApi = {
  list: (params) => api.get('/entries', { params }),
  create: (data) => api.post('/entries', data),
  update: (id, data) => api.put(`/entries/${id}`, data),
  delete: (id) => api.delete(`/entries/${id}`),
  bulkDelete: (entryIds) => api.post('/entries/bulk-delete', { entry_ids: entryIds }),
  copy: (id) => api.post(`/entries/${id}/copy`),
  bulkValidate: (rows) => api.post('/entries/bulk-validate', { rows }),
  bulkUpload: (rows) => api.post('/entries/bulk-upload', { rows }),
};

// Attachments
export const attachmentsApi = {
  list: (entryId) => api.get(`/entries/${entryId}/attachments`),
  upload: (entryId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/entries/${entryId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  delete: (fileId) => api.delete(`/attachments/${fileId}`),
  getUrl: (path) => `${API_URL}/api/files/${path}?auth=${localStorage.getItem('access_token') || ''}`,
};

// Dashboard
export const dashboardApi = {
  get: (siteId) => api.get('/dashboard', { params: siteId ? { site_id: siteId } : {} }),
};

// Ledger
export const ledgerApi = {
  get: (params) => api.get('/ledger', { params }),
  combined: (params) => api.get('/ledger/combined', { params }),
};

// Export
export const exportApi = {
  excel: (params) => `${API_URL}/api/export/excel?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
  pdf: (params) => `${API_URL}/api/export/pdf?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
  excelDrCr: (params) => `${API_URL}/api/export/excel-drcr?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
  pdfDrCr: (params) => `${API_URL}/api/export/pdf-drcr?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
  combinedExcel: (params) => `${API_URL}/api/export/combined-excel?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
  combinedPdf: (params) => `${API_URL}/api/export/combined-pdf?${new URLSearchParams({...params, auth: localStorage.getItem('access_token') || ''}).toString()}`,
};

// Cash & Bank Flow
export const cashBankApi = {
  get: (siteId) => api.get('/cash-bank-flow', { params: siteId ? { site_id: siteId } : {} }),
};

// Audit Logs
export const auditApi = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Partner Accounts
export const partnerAccountsApi = {
  list: (partnerId) => api.get('/partner-accounts', { params: partnerId ? { partner_id: partnerId } : {} }),
  create: (data) => api.post('/partner-accounts', data),
  update: (id, data) => api.put(`/partner-accounts/${id}`, data),
  delete: (id) => api.delete(`/partner-accounts/${id}`),
};

// Transfers
export const transfersApi = {
  list: (params) => api.get('/transfers', { params }),
  get: (id) => api.get(`/transfers/${id}`),
  create: (data) => api.post('/transfers', data),
  addStep: (id, data) => api.post(`/transfers/${id}/steps`, data),
  complete: (id) => api.put(`/transfers/${id}/complete`),
  delete: (id) => api.delete(`/transfers/${id}`),
};

// User Management
export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  updateRole: (id, data) => api.put(`/users/${id}/role`, data),
  updateAccess: (id, data) => api.put(`/users/${id}/access`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Entity Bank Accounts
export const entityBankAccountsApi = {
  list: (entityId) => api.get('/entity-bank-accounts', { params: entityId ? { entity_id: entityId } : {} }),
  listAll: () => api.get('/entity-bank-accounts'),
  create: (data) => api.post('/entity-bank-accounts', data),
  update: (id, data) => api.put(`/entity-bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/entity-bank-accounts/${id}`),
};

// Template
export const templateApi = {
  csv: () => `${API_URL}/api/template/csv?auth=${localStorage.getItem('access_token') || ''}`,
};

// PDF Statement parsing
export const statementApi = {
  parsePdf: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/parse-pdf-statement', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  suggestEntity: (text, category) => api.get('/suggest-entity', { params: { text, ...(category ? { category } : {}) } }),
  learnMappings: (mappings) => api.post('/entity-mappings/learn', { mappings }),
  getMappingStats: () => api.get('/entity-mappings/stats'),
};

export default api;
